import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Leaf, FlaskConical, ShieldCheck, Sparkles, Rabbit, ArrowRight, Star } from "lucide-react";
import api, { img, money } from "../lib/api";
import { MEDIA } from "../lib/media";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import ProductCard from "../components/ProductCard";

const TREASURES = [
  { title: "Living Root Bridges", copy: "Strength that grows naturally.", img: MEDIA.rootbridge },
  { title: "Sacred Forests", copy: "Protected by nature.", img: MEDIA.fern },
  { title: "Crystal Waterfalls", copy: "Pure. Fresh. Gentle.", img: MEDIA.waterfall },
  { title: "Morning Mist", copy: "Hydration in every drop.", img: MEDIA.mist },
];

const WHY = [
  { icon: Leaf, label: "Nature Inspired" },
  { icon: FlaskConical, label: "Thoughtful Formulations" },
  { icon: Sparkles, label: "Clean Ingredients" },
  { icon: ShieldCheck, label: "Mindful Manufacturing" },
  { icon: Rabbit, label: "Cruelty Free" },
];

export default function Home() {
  const [bestsellers, setBestsellers] = useState([]);
  const [collections, setCollections] = useState([]);
  const [rituals, setRituals] = useState([]);
  const [articles, setArticles] = useState([]);
  const [reviews, setReviews] = useState([]);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);

  useEffect(() => {
    api.get("/products?sort=best-selling&limit=8").then(r => setBestsellers(r.data.filter(p => p.is_bestseller).slice(0, 4).length ? r.data.filter(p => p.is_bestseller).slice(0, 4) : r.data.slice(0, 4)));
    api.get("/collections?kind=category").then(r => setCollections(r.data));
    api.get("/rituals").then(r => setRituals(r.data));
    api.get("/journal").then(r => setArticles(r.data.slice(0, 3)));
    Promise.all(["ratri-amrut-elixir", "glow-dew-gel-face-wash", "collagen-booster-serum"]
      .map(s => api.get(`/products/${s}/reviews`).then(r => r.data[0]).catch(() => null)))
      .then(rs => setReviews(rs.filter(Boolean)));
  }, []);

  const catImg = { face: MEDIA.face, hair: MEDIA.hair, body: MEDIA.body, wellness: MEDIA.ritual };

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden flex items-end">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img src={MEDIA.hero} alt="Misty mountains of Meghalaya" className="h-[110%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-forest/70 via-forest/10 to-transparent" />
        </motion.div>
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 pb-16 lg:pb-24 w-full">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="max-w-2xl">
            <span className="eyebrow text-ivory/80">Nature. Purity. Essence.</span>
            <h1 className="serif text-ivory text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mt-4 mb-6">Nature's Finest.<br />Inspired by Meghalaya.</h1>
            <p className="text-ivory/80 text-base lg:text-lg max-w-lg leading-relaxed mb-8">Discover skincare crafted with thoughtful formulations, inspired by the pristine forests, misty mountains and timeless beauty of Meghalaya.</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" className="bg-ivory text-forest px-8 py-4 rounded-[9px] uppercase text-xs tracking-[0.18em] font-medium hover:bg-sage hover:-translate-y-0.5 transition-all duration-300" data-testid="hero-explore">Explore Collection</Link>
              <Link to="/our-story" className="border border-ivory/60 text-ivory px-8 py-4 rounded-[9px] uppercase text-xs tracking-[0.18em] font-medium hover:bg-ivory/10 hover:-translate-y-0.5 transition-all duration-300" data-testid="hero-story">Discover Our Story</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-sage/30">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
          {WHY.map((w) => (
            <div key={w.label} className="flex items-center gap-2.5 text-forest/70">
              <w.icon size={17} strokeWidth={1.4} />
              <span className="text-xs uppercase tracking-[0.14em]">{w.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* MEGHALAYA STORY */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="eyebrow text-moss">The Land</span>
            <h2 className="serif text-4xl lg:text-5xl leading-[1.12] mt-4 mb-6">Meghalaya — Where Nature Breathes Differently</h2>
            <p className="text-forest/70 leading-relaxed mb-4">Hidden in the clouds of Northeast India lies Meghalaya — a land of ancient forests, living root bridges, waterfalls and remarkable biodiversity.</p>
            <p className="text-forest/70 leading-relaxed mb-8">Its harmony with nature inspires the world around us and shapes the philosophy behind IAH.</p>
            <Link to="/meghalaya" className="btn-outline" data-testid="story-discover">Discover Our Story</Link>
          </Reveal>
          <div className="grid grid-cols-2 gap-4">
            {[MEDIA.waterfall, MEDIA.fern, MEDIA.mist, MEDIA.rootbridge].map((src, i) => (
              <Reveal key={i} delay={i * 0.08} className={i % 3 === 0 ? "mt-8" : ""}>
                <div className="overflow-hidden aspect-[3/4]"><img src={src} alt="" className="h-full w-full object-cover hover:scale-105 transition-transform duration-700" /></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TREASURES */}
      <section className="bg-beige/40 py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <Reveal className="max-w-2xl mb-14">
            <span className="eyebrow text-moss">Storytelling</span>
            <h2 className="serif text-4xl lg:text-5xl leading-tight mt-3">Inspired by Meghalaya's Natural Treasures</h2>
          </Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TREASURES.map((t) => (
              <StaggerItem key={t.title}>
                <div className="group relative overflow-hidden aspect-[3/4]">
                  <img src={t.img} alt={t.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/80 to-transparent" />
                  <div className="absolute bottom-0 p-6 text-ivory">
                    <h3 className="serif text-2xl mb-1">{t.title}</h3>
                    <p className="text-sm text-ivory/80">{t.copy}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <p className="text-xs text-forest/40 mt-6 max-w-xl">These are storytelling concepts inspired by Meghalaya and do not imply that products contain these specific elements.</p>
        </div>
      </section>

      {/* COLLECTIONS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32">
        <Reveal className="flex items-end justify-between mb-14">
          <div><span className="eyebrow text-moss">Explore</span><h2 className="serif text-4xl lg:text-5xl mt-3">Our Collections</h2></div>
          <Link to="/shop" className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-forest/70 hover:text-forest">View All <ArrowRight size={15} /></Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {(collections.length ? collections : [{ slug: "face", title: "Face" }, { slug: "hair", title: "Hair" }, { slug: "body", title: "Body" }, { slug: "wellness", title: "Wellness" }]).map((c) => (
            <Reveal key={c.slug}>
              <Link to={`/collections/${c.slug}`} className="group block relative overflow-hidden aspect-[4/5]" data-testid={`home-collection-${c.slug}`}>
                <img src={catImg[c.slug] || MEDIA.ritual} alt={c.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-forest/25 group-hover:bg-forest/35 transition-colors" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="serif text-3xl lg:text-4xl text-ivory tracking-wide">{c.title}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SHOP BY RITUAL */}
      <section className="bg-forest text-ivory relative grain overflow-hidden py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 relative">
          <Reveal className="mb-14"><span className="eyebrow text-sage">Ritual</span><h2 className="serif text-4xl lg:text-5xl mt-3">Shop by Ritual</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-ivory/10">
            {rituals.map((r) => (
              <Link key={r.slug} to={`/rituals#${r.slug}`} className="group bg-forest p-8 hover:bg-moss transition-colors" data-testid={`ritual-${r.slug}`}>
                <div className="serif text-3xl mb-3">{r.title}</div>
                <p className="text-sm text-ivory/60 mb-6">{r.subtitle}</p>
                <span className="text-xs uppercase tracking-[0.16em] flex items-center gap-2 text-sage group-hover:text-ivory">Shop <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32">
        <Reveal className="flex items-end justify-between mb-14">
          <div><span className="eyebrow text-moss">Loved Most</span><h2 className="serif text-4xl lg:text-5xl mt-3">Best Sellers</h2></div>
          <Link to="/collections/best-sellers" className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-forest/70 hover:text-forest">View All <ArrowRight size={15} /></Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
          {bestsellers.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
        </div>
      </section>

      {/* FEATURED BANNER */}
      <section className="relative overflow-hidden">
        <img src={MEDIA.ritual} alt="The Glow Ritual" className="h-[60vh] min-h-[420px] w-full object-cover" />
        <div className="absolute inset-0 bg-forest/35" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 w-full">
            <Reveal className="max-w-lg text-ivory">
              <span className="eyebrow text-ivory/80">Featured Collection</span>
              <h2 className="serif text-5xl lg:text-6xl mt-4 mb-5">The Glow Ritual</h2>
              <p className="text-ivory/80 mb-8 leading-relaxed">Thoughtfully curated essentials for your everyday glow.</p>
              <Link to="/collections/glow-collection" className="bg-ivory text-forest px-8 py-4 rounded-[9px] uppercase text-xs tracking-[0.18em] font-medium hover:bg-sage hover:-translate-y-0.5 transition-all duration-300" data-testid="featured-shop">Shop the Collection</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* JOURNAL */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24 lg:py-32">
        <Reveal className="mb-14"><span className="eyebrow text-moss">Editorial</span><h2 className="serif text-4xl lg:text-5xl mt-3">Meghalaya Journal</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-8">
          {articles.map((a) => (
            <Reveal key={a.slug}>
              <Link to={`/journal/${a.slug}`} className="group block" data-testid={`journal-${a.slug}`}>
                <div className="overflow-hidden aspect-[4/3] mb-5"><img src={img(a.cover)} alt={a.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <span className="eyebrow text-moss">{a.category}</span>
                <h3 className="serif text-2xl mt-2 mb-2 group-hover:opacity-70 transition-opacity">{a.title}</h3>
                <p className="text-sm text-forest/60 line-clamp-2">{a.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      {reviews.length > 0 && (
        <section className="bg-beige/40 py-24">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
            <Reveal className="text-center mb-14"><span className="eyebrow text-moss">Loved By You</span><h2 className="serif text-4xl lg:text-5xl mt-3">In Their Words</h2></Reveal>
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.map((r, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="bg-ivory p-8 h-full border border-sage/20">
                    <div className="flex gap-1 mb-4">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={14} className="fill-forest text-forest" />)}</div>
                    <p className="serif text-2xl leading-snug mb-6">"{r.title}"</p>
                    <p className="text-sm text-forest/65 mb-6">{r.body}</p>
                    <div className="text-xs uppercase tracking-[0.14em] text-forest/50">{r.name} {r.verified && "· Verified"}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MEGHALAYA STORY FULLWIDTH */}
      <section className="relative overflow-hidden">
        <img src={MEDIA.storyFull} alt="Sunrise over Meghalaya" className="h-[70vh] min-h-[480px] w-full object-cover" />
        <div className="absolute inset-0 bg-forest/45" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <Reveal className="max-w-2xl text-ivory">
            <span className="eyebrow text-ivory/80">The Meghalaya Story</span>
            <p className="serif text-3xl lg:text-4xl leading-snug mt-5">Every sunrise over Meghalaya's hills reminds us that beauty doesn't need to be complicated. It begins with purity. It grows through consistency. It lasts through care.</p>
          </Reveal>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <Reveal className="text-center mb-12"><span className="eyebrow text-moss">@iah.skincare</span><h2 className="serif text-4xl mt-3">Follow the Ritual</h2></Reveal>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
          {[MEDIA.face, MEDIA.fern, MEDIA.hair, MEDIA.mist, MEDIA.body, MEDIA.waterfall].map((src, i) => (
            <a key={i} href={(reviews && "https://instagram.com") || "#"} target="_blank" rel="noreferrer" className="group block overflow-hidden aspect-square">
              <img src={src} alt="" className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-700" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
